<template>
  <div class="member-collection">
    <member-collection-hero
      v-bind="content?.lead"
      :screen-width="screenWidth"
    />
    <featured-story
      v-if="!!content?.featuredStory"
      :resource="content?.featuredStory"
      data-ui="featured-story"
    />
    <member-promo-group
      v-if="!!content?.secondaryStories"
      v-bind="content?.secondaryStories"
      data-ui="secondary-featured-story"
    />
    <collection-carousel
      v-for="(collection, index) in productCollectionModels"
      v-bind="collection"
      :key="index"
      :data-ui="`product-carousel-${index}`"
    />
    <div class="benefits">
      <member-collection-benefits
        v-bind="benefitsModel"
        :membership-in-cart="membershipAddedToCart"
        @error="(message) => handleError('error', message)"
        @warn="(message) => handleError('warn', message)"
        @success="handleSuccess"
      />
    </div>
    <added-to-cart-modal
      :is-open-override="atcModalOpenOverride"
      :location="analyticsViewData.pageSection"
      @close="handleClose"
    />
  </div>
</template>

<script>
import { getUser } from '@rei/session';
import { useDynamicContent } from '@rei/cs-preview-fetch';
import MemberCollectionHero from './components/MemberCollectionHero.vue';
import MemberCollectionBenefits from './components/MemberCollectionBenefits.vue';
import FeaturedStory from './components/FeaturedStory.vue';
import CollectionCarousel from '../../common/components/collection-carousel/CollectionCarousel.vue';
import AddedToCartModal from '../../common/components/added-to-cart-modal/AddedToCartModal.vue';
import MemberPromoGroup from '../../common/components/member-promo/MemberPromoGroup.vue';
import analyticsMixin from '../../common/mixins/analyticsMixin';
import { getCartInformation } from '../../utils/cartController';
import {
  getProductSlidesModel,
} from '../../utils/member-collection-product-collection-model-adapter';
import { PRODUCT_VIEW } from '../../utils/analytics-constants';

export default {
  name: 'MemberCollectionPage',
  components: {
    AddedToCartModal,
    CollectionCarousel,
    FeaturedStory,
    MemberCollectionBenefits,
    MemberCollectionHero,
    MemberPromoGroup,
  },
  mixins: [
    analyticsMixin,
  ],
  provide() {
    const homePageInjectables = {};
    const userInformation = {};

    Object.defineProperty(homePageInjectables, 'addMembershipToCart', {
      enumerable: true,
      get: () => (this.membershipCtaText || ''),
    });

    Object.defineProperty(userInformation, 'user', {
      enumerable: true,
      get: () => (this.user || {}),
    });
    return {
      homePageInjectables,
      userInformation,
    };
  },
  props: {
    appProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    additionalData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    componentModels: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const getPreviewUrl = () => '/membership/member-collection/preview';
    const { content } = useDynamicContent(props, getPreviewUrl);
    if (content.value?.componentModels) {
      content.value = content.value.componentModels;
    }
    return {
      content,
    };
  },
  data() {
    return {
      screenWidth: 375, // mobile-first ya dig
      atcModalOpenOverride: false,
      membershipAddedToCart: false,
      user: {},
    };
  },
  computed: {
    /* c8 ignore start */
    analyticsViewData() {
      return {
        ...PRODUCT_VIEW,
        pageSection: 'collection',
      };
    },
    /* c8 ignore end */
    featureToggles() {
      return this.appProps?.['feature.toggles'] || {};
    },
    membershipCtaText() {
      return this.appProps['frontend.strings'].addToCart;
    },
    benefitsModel() {
      return this.content?.memberBenefits;
    },
    productCollectionModels() {
      const { screenWidth } = this;
      return this.content?.collections.map((collection) => ({
        ctaHref: collection.ctaHref,
        ctaText: collection.ctaText,
        heading: collection.heading,
        subheading: collection.subheading,
        screenWidth,
        id: collection.ctaHref,
        slides: getProductSlidesModel(collection.searchResultsDetails?.productResults),
        total: collection.searchResultsDetails?.totalResults,
      }));
    },
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize);
      this.onResize();
    }
  },
  async created() {
    if (typeof window !== 'undefined' && window.reiSession) {
      const { isMembershipAddedToCart } = await getCartInformation();
      this.membershipAddedToCart = isMembershipAddedToCart;

      try {
        this.getUserAccountData();
      } catch (error) {
        console.error(error);
      }
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
    }
  },
  methods: {
    onResize() {
      this.screenWidth = document.body.clientWidth;
    },
    /* c8 ignore start */
    handleError(type, message) {
      this.metricsLink(`${type}_add membership to cart_${message}`);
    },
    /* c8 ignore end */
    handleSuccess() {
      this.atcModalOpenOverride = true;
      this.membershipAddedToCart = true;
    },
    handleClose() {
      this.atcModalOpenOverride = false;
    },
    getUserAccountData() {
      return new Promise((reject) => {
        this.loading = false;
        getUser().then((user) => {
          this.user = this.populateUserInformation(user);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    populateUserInformation(response) {
      let user = {};
      if (response) {
        if (response.contact) {
          user = {
            ...user,
            firstName: response.contact.firstName,
            lastName: response.contact.lastName,
          };
        }
        if (response.membership && response.membership.memberNumber) {
          user = {
            ...user,
            ...response.membership,
            isMember: true,
          };
        }
        return {
          ...user,
          isLoggedIn: response.isLoggedIn,
        };
      }
      return user;
    },
  },
};
</script>

<style lang="scss">

.carousel,
.collection,
.potm {
  @include cdr-container;
  margin: 0 auto;
  min-height: 120px;
  border-bottom: 1px solid lightgray;
}

.member-exclusive-promo {
  @include cdr-container;
}
/* give enough space so box-shadow doesn't overlap overflow: hidden */
$benefit-card-overflow-spacing: 12px;

.member-collection-benefits__cards {
  margin-bottom: (-1 * $benefit-card-overflow-spacing);
}
.step-card__inner {
  margin-bottom: $benefit-card-overflow-spacing;
}
</style>
